import React from 'react';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

export default function Pagination({ page, pages, total, limit, onPageChange, showInfo = true, className = '' }) {
  if (pages <= 1) return null;

  const getPages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;
    for (let i = 1; i <= pages; i++) {
      if (i === 1 || i === pages || (i >= page - delta && i <= page + delta)) {
        range.push(i);
      }
    }
    for (let i of range) {
      if (l) {
        if (i - l === 2) rangeWithDots.push(l + 1);
        else if (i - l !== 1) rangeWithDots.push('...');
      }
      rangeWithDots.push(i);
      l = i;
    }
    return rangeWithDots;
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-3 ${className}`}>
      {showInfo && (
        <p className="text-sm text-muted-foreground order-2 sm:order-1">
          Showing <span className="font-medium">{start}</span>–<span className="font-medium">{end}</span> of{' '}
          <span className="font-medium">{total}</span> results
        </p>
      )}
      <div className="flex items-center gap-1 flex-wrap justify-center order-1 sm:order-2">
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0 hidden sm:flex"
          onClick={() => onPageChange(1)}
          disabled={page === 1}
          aria-label="First page"
        >
          <ChevronsLeft className="w-3.5 h-3.5" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </Button>

        {getPages().map((p, i) =>
          p === '...' ? (
            <span key={`dot-${i}`} className="px-1.5 py-1 text-sm text-muted-foreground hidden sm:inline">…</span>
          ) : (
            <Button
              key={p}
              variant={p === page ? 'default' : 'outline'}
              size="sm"
              className={`h-8 w-8 p-0 text-xs ${p === page ? 'bg-[#FF4500] hover:bg-[#E63F00] border-[#FF4500] text-white' : ''} ${Math.abs(p - page) > 2 && p !== 1 && p !== pages ? 'hidden sm:flex' : ''}`}
              onClick={() => onPageChange(p)}
              data-testid={`page-btn-${p}`}
            >
              {p}
            </Button>
          )
        )}

        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => onPageChange(page + 1)}
          disabled={page === pages}
          aria-label="Next page"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0 hidden sm:flex"
          onClick={() => onPageChange(pages)}
          disabled={page === pages}
          aria-label="Last page"
        >
          <ChevronsRight className="w-3.5 h-3.5" />
        </Button>
        <span className="text-xs text-muted-foreground sm:hidden ml-1">Page {page}/{pages}</span>
      </div>
    </div>
  );
}
